import { Button, Checkbox, Col, Form, Input, message, Row, Select, Tooltip, Upload } from 'antd';
import { ICapturePaymentFormInputFields } from '../OrderInfoSlider/utils';
import { CheckCircleFilled, UploadOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styles from './index.module.scss';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { apiD2r } from 'shared/api';
import errorHandler from 'shared/utils/errorHandler';
import { Loading } from '../Loading';
import { antDesignValidator } from 'helper';

interface IPaymentForm {
	orderId: string;
	dummyReq: (options: any) => void;
	getPaymentLogs: () => void;
	onPaymentCaptured?: () => void;
}

type IChangedValue = {
	[key: string]: string;
};

interface IformData {
	amount: string;
	mode: string;
	stockReturnAmount?: number;
}

enum D2RPaymentType {
	UPI = 'UPI',
	CASH = 'CASH',
	CHEQUE = 'CHEQUE',
	BANK_TRANSFER = 'BANK_TRANSFER',
	CN_ADJUSTED = 'CN-ADJUSTED'
}

export const PaymentForm = ({ orderId, dummyReq, getPaymentLogs, onPaymentCaptured }: IPaymentForm) => {
	const [form] = Form.useForm();
	const [isAmountZero, setIsAmountZero] = useState(true);
	const [file, setFile] = useState({});
	const [itemUploadStatus, setItemUploadStatus] = useState<boolean>(false);
	const [isInvoiceClosed, setIsInvoiceClosed] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);

	const normalizeAmount = (value: string): number => {
		if (!value || value === '') return 0;
		return Number(value);
	};

	const onFileChange = ({ fileList }: any) => {
		try {
			const proof = fileList[0]?.originFileObj;

			//becauase for one upload onFileChange is called many times, so to avoid it
			if (!proof) return;

			const img = new Image();
			img.src = URL.createObjectURL(proof);
			setFile({});

			img.onload = () => {
				let reader = new FileReader();
				let result;

				//after async reading of the file sets the result to the result variable inside reader
				reader.onloadend = () => {
					result = reader.result;

					setFile({ data: reader.result, name: proof.name, url: img.src });
				};

				//this function reads the file in a async way and gives back a base64 data in the result
				reader.readAsDataURL(proof);
				return result;
			};
		} catch (error) {
			errorHandler(error);
		}
	};

	const handleValuesChange = (changedValues: IChangedValue) => {
		if (changedValues.amount) {
			const rawAmount = changedValues.amount;
			const normalizedAmount = normalizeAmount(rawAmount);
			setIsAmountZero(normalizedAmount === 0);
		}
	};

	async function capturePayment(formData: IformData) {
		try {
			setLoading(true);
			const response = await apiD2r.post('/capture-payment', {
				id: orderId,
				proof: file,
				amount: Number(formData.amount),
				mode: formData.mode,
				stockReturnAmount: Number(formData.stockReturnAmount || 0),
				isInvoiceClosed
			});

			if (response.data.status) {
				message.success('Payment captured successfully');
				onPaymentCaptured?.();
				getPaymentLogs();
			}
		} catch (error) {
			errorHandler(error);
		} finally {
			setLoading(false);
		}
	}

	const onCheckBoxChange = (e: CheckboxChangeEvent) => {
		setIsInvoiceClosed(e.target.checked);
	};

	if (loading) {
		return <Loading />;
	}
	return (
		<Form
			form={form}
			layout="horizontal"
			style={{ marginTop: '2rem' }}
			onFinish={capturePayment}
			onValuesChange={handleValuesChange}
			className={styles.formContainer}
		>
			{ICapturePaymentFormInputFields.map((e) => (
				<Form.Item rules={e.rules} key={e.key} name={e.key} label="Enter Amount" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
					<Input type={e.type} placeholder={e.placeholder} autoFocus style={{ padding: '8px' }} />
				</Form.Item>
			))}
			<Row gutter={16}>
				<Col span={16}>
					<Form.Item
						name="mode"
						rules={[
							() => ({
								required: !isAmountZero,
								message: 'Please select payment mode'
							})
						]}
					>
						<Select placeholder="Select Payment Mode" disabled={isAmountZero} style={{ width: '352px' }}>
							{Object.values(D2RPaymentType).map((mode) => (
								<Select.Option key={mode} value={mode}>
									{mode}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
			</Row>

			<Form.Item style={{ marginBottom: '10px' }}>
				<Row gutter={16} style={{ display: 'flex', flexDirection: 'row' }}>
					<Col span={12}>
						<span>Upload Proof</span>
						<Tooltip title="Image size should be less than 600 KB" placement="right">
							<Upload
								customRequest={dummyReq}
								accept="image/*"
								multiple={false}
								onChange={({ fileList }) => {
									onFileChange({ fileList });
									setItemUploadStatus(true);
								}}
								style={{ marginBlock: '15px' }}
								showUploadList={false}
							>
								<Button
									type="text"
									size="middle"
									className={styles.proof}
									disabled={isAmountZero}
									icon={itemUploadStatus ? <CheckCircleFilled style={{ color: '#228B22' }} /> : <UploadOutlined />}
								></Button>
							</Upload>
						</Tooltip>{' '}
					</Col>

					<Col span={12} style={{ width: '170px' }}>
						<Form.Item valuePropName="checked">
							<Checkbox onChange={onCheckBoxChange} disabled={isAmountZero} className={styles.markClose}>
								Mark as Closed
							</Checkbox>
						</Form.Item>
					</Col>

					{isInvoiceClosed && (
						<Col span={24} style={{ padding: 10 }}>
							<Form.Item
								name="stockReturnAmount"
								label="Enter Stock Return Amount"
								rules={[
									{
										required: false,
										message: ''
									},
									antDesignValidator.positiveNumber
								]}
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
							>
								<Input type={'number'} placeholder={'Enter Return Amount'} autoFocus style={{ padding: '8px' }} />
							</Form.Item>
						</Col>
					)}
				</Row>
			</Form.Item>

			<Form.Item>
				<Button type="primary" htmlType="submit" style={{ width: '100%' }} className={styles.submitBtn}>
					Submit
				</Button>
			</Form.Item>
		</Form>
	);
};
